/**
 * Core Web Vitals reporter.
 *
 * Measures LCP, CLS and INP (plus FCP and TTFB when diagnostics are enabled)
 * with native PerformanceObserver APIs and pushes ONE `web_vitals` event per
 * page view to `window.dataLayer`, carrying every metric. The event is sent
 * SEND_DELAY_MS after the window `load` event, so CLS and INP cover the
 * loading phase and any interactions up to that point — not the full visit.
 *
 * Only a sample of page views is measured (`data-sample-rate`, in %).
 * `?wv_debug` in the URL always measures and logs live values to the console.
 */

/**
 * @typedef {'LCP'|'CLS'|'INP'|'FCP'|'TTFB'} MetricName
 */

/** How long after the window `load` event the metrics are sent */
const SEND_DELAY_MS = 5000;

if (!customElements.get('web-vitals')) {
  customElements.define(
    'web-vitals',
    class WebVitals extends HTMLElement {
      debug = new URL(window.location.href).searchParams.has('wv_debug');
      sent = false;
      sendTimer = null;
      observers = {};
      firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
      lcpEntry = null;
      fcpEntry = null;
      clsSession = { value: 0, entries: [] };
      clsWorst = { value: 0, entries: [] };
      /** @type {Map<number, number>} Longest event duration (ms) per interaction id */
      interactions = new Map();

      connectedCallback() {
        if (!('PerformanceObserver' in window)) return;
        if (!this.debug && !this.isSampled()) return;

        window.dataLayer = window.dataLayer || [];

        this.observers.lcp = this.observe('largest-contentful-paint', this.onLargestContentfulPaint);
        this.observers.cls = this.observe('layout-shift', this.onLayoutShift);
        this.observers.inp = this.observe('event', this.onEventTiming, { durationThreshold: 40 });
        this.observers.firstInput = this.observe('first-input', this.onEventTiming);

        if (this.hasDiagnostics) {
          this.observers.fcp = this.observe('paint', this.onPaint);
        }

        this.setupEventListeners();

        if (document.readyState === 'complete') {
          this.onLoad();
        } else {
          window.addEventListener('load', this.onLoad, { once: true });
        }
      }

      disconnectedCallback() {
        clearTimeout(this.sendTimer);
        this.disconnectObservers();
        this.removeEventListeners();
      }

      setupEventListeners() {
        document.addEventListener('visibilitychange', this.onVisibilityChange, true);
        window.addEventListener('keydown', this.onFirstInput, { capture: true, once: true });
        window.addEventListener('pointerdown', this.onFirstInput, { capture: true, once: true });
      }

      removeEventListeners() {
        document.removeEventListener('visibilitychange', this.onVisibilityChange, true);
        window.removeEventListener('load', this.onLoad);
        window.removeEventListener('keydown', this.onFirstInput, true);
        window.removeEventListener('pointerdown', this.onFirstInput, true);
      }

      disconnectObservers() {
        for (const observer of Object.values(this.observers)) {
          observer?.disconnect();
        }
      }

      get hasDiagnostics() {
        return this.dataset.diagnostics === 'true';
      }

      /** Time the navigation became active (non-zero for prerendered pages) */
      get activationStart() {
        return performance.getEntriesByType('navigation')[0]?.activationStart || 0;
      }

      /**
       * Decides once per page view whether this visit is measured
       * @returns {boolean}
       */
      isSampled() {
        const sampleRate = Number.parseFloat(this.dataset.sampleRate);
        if (Number.isNaN(sampleRate)) return true;

        return Math.random() * 100 < Math.min(Math.max(sampleRate, 0), 100);
      }

      /**
       * Starts a buffered PerformanceObserver for an entry type, if supported
       * @param {string} type - Performance entry type
       * @param {(entries: PerformanceEntry[]) => void} callback - Receives each batch of entries
       * @param {Object} [options] - Extra observe() options
       * @returns {PerformanceObserver|undefined}
       */
      observe(type, callback, options = {}) {
        if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;

        try {
          const observer = new PerformanceObserver((list) => callback(list.getEntries()));
          observer.observe({ type, buffered: true, ...options });
          return observer;
        } catch (error) {
          console.error(`web-vitals: unable to observe ${type}`, error);
        }
      }

      /**
       * Delivers entries the browser has queued but not yet passed to an observer
       * @param {PerformanceObserver|undefined} observer
       * @param {(entries: PerformanceEntry[]) => void} callback
       */
      flush(observer, callback) {
        if (!observer) return;
        callback(observer.takeRecords());
      }

      // Event handlers

      onLoad = () => {
        this.sendTimer = setTimeout(() => this.send(), SEND_DELAY_MS);
      };

      /** Paints after the page was first hidden don't count towards LCP/FCP */
      onVisibilityChange = (e) => {
        if (document.visibilityState !== 'hidden') return;
        this.firstHiddenTime = Math.min(this.firstHiddenTime, e.timeStamp);
      };

      /** The browser stops updating LCP after the first input, so freeze it here */
      onFirstInput = () => {
        this.flush(this.observers.lcp, this.onLargestContentfulPaint);
        this.observers.lcp?.disconnect();
        this.observers.lcp = undefined;
      };

      onLargestContentfulPaint = (entries) => {
        for (const entry of entries) {
          if (entry.startTime >= this.firstHiddenTime) continue;

          this.lcpEntry = entry;
          this.logLive('LCP', `${Math.round(entry.startTime)}ms`, entry.element || entry.url);
        }
      };

      /**
       * Groups layout shifts into session windows (max 1s gap, max 5s span)
       * and keeps the worst window — that window's total is the page's CLS.
       */
      onLayoutShift = (entries) => {
        for (const entry of entries) {
          if (entry.hadRecentInput) continue;

          const firstEntry = this.clsSession.entries[0];
          const lastEntry = this.clsSession.entries.at(-1);
          const continuesSession =
            lastEntry && entry.startTime - lastEntry.startTime < 1000 && entry.startTime - firstEntry.startTime < 5000;

          this.clsSession = continuesSession
            ? { value: this.clsSession.value + entry.value, entries: [...this.clsSession.entries, entry] }
            : { value: entry.value, entries: [entry] };

          if (this.clsSession.value > this.clsWorst.value) this.clsWorst = this.clsSession;

          this.logLive('CLS', `+${entry.value.toFixed(3)} → ${this.clsWorst.value.toFixed(3)}`, entry.sources?.[0]?.node);
        }
      };

      /** Keeps the longest event duration seen for each user interaction */
      onEventTiming = (entries) => {
        for (const entry of entries) {
          if (!entry.interactionId) continue;

          const existing = this.interactions.get(entry.interactionId) ?? 0;
          if (entry.duration > existing) this.interactions.set(entry.interactionId, entry.duration);
        }
      };

      onPaint = (entries) => {
        const fcpEntry = entries.find((entry) => entry.name === 'first-contentful-paint');
        if (!fcpEntry || fcpEntry.startTime >= this.firstHiddenTime) return;

        this.fcpEntry = fcpEntry;
        this.observers.fcp?.disconnect();
        this.observers.fcp = undefined;
      };

      // Metric calculation — each returns ms (CLS: unitless score), or null when unavailable

      getLCP() {
        this.flush(this.observers.lcp, this.onLargestContentfulPaint);
        if (!this.lcpEntry) return null;

        return Math.max(this.lcpEntry.startTime - this.activationStart, 0);
      }

      getCLS() {
        if (!this.observers.cls) return null;

        this.flush(this.observers.cls, this.onLayoutShift);
        return this.clsWorst.value;
      }

      /**
       * INP is the worst interaction, ignoring one outlier per 50 interactions
       * so a single unusual event on a long-lived page does not dominate.
       */
      getINP() {
        this.flush(this.observers.inp, this.onEventTiming);
        this.flush(this.observers.firstInput, this.onEventTiming);
        if (!this.interactions.size) return null;

        const sorted = [...this.interactions.values()].sort((a, b) => b - a);
        return sorted[Math.min(Math.floor(this.interactions.size / 50), sorted.length - 1)];
      }

      getFCP() {
        this.flush(this.observers.fcp, this.onPaint);
        if (!this.fcpEntry) return null;

        return Math.max(this.fcpEntry.startTime - this.activationStart, 0);
      }

      getTTFB() {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (!navigation || navigation.responseStart <= 0) return null;

        return Math.max(navigation.responseStart - this.activationStart, 0);
      }

      // Reporting

      /** Pushes the single `web_vitals` event for this page view */
      send() {
        if (this.sent) return;
        this.sent = true;

        const metrics = {
          LCP: this.getLCP(),
          CLS: this.getCLS(),
          INP: this.getINP(),
          ...(this.hasDiagnostics && { FCP: this.getFCP(), TTFB: this.getTTFB() }),
        };

        this.disconnectObservers();
        this.removeEventListeners();

        const data = { event: 'web_vitals', theme_type: this.dataset.themeType, page_template: this.dataset.template };

        for (const [name, value] of Object.entries(metrics)) {
          if (value === null) continue;
          data[`${name.toLowerCase()}_value`] = Math.round(name === 'CLS' ? value * 1000 : value);
        }

        window.dataLayer.push(data);

        console.groupCollapsed(`[web-vitals] dataLayer.push → ${this.formatSummary(metrics)}`);
        console.table(data);
        console.groupEnd();
      }

      /**
       * One-line console summary, e.g. "LCP 1834ms · CLS 0.041 · INP –"
       * @param {Object<string, number|null>} metrics
       * @returns {string}
       */
      formatSummary(metrics) {
        const parts = [];

        for (const [name, value] of Object.entries(metrics)) {
          if (value === null) {
            parts.push(`${name} –`);
            continue;
          }

          parts.push(`${name} ${name === 'CLS' ? value.toFixed(3) : `${Math.round(value)}ms`}`);
        }

        return parts.join(' · ');
      }

      /**
       * Logs an in-progress metric value in debug mode. Console only — never sent to the dataLayer.
       * @param {MetricName} name
       * @param {string} value - Formatted value to display
       * @param {Node|string|null|undefined} source - Element (or URL) behind the change
       */
      logLive(name, value, source) {
        if (!this.debug || this.sent) return;

        console.log(`[web-vitals] (live) ${name}: ${value}`, source ?? '');
      }
    }
  );
}
